import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface ClientsContextType {
  clients: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchClients: (page?: number, perPage?: number) => Promise<void>;
  addClient: (client: any) => Promise<void>;
  editClient: (client: any) => Promise<void>;
  deleteClient: (id: number | string) => Promise<void>;
}

export const ClientsContext = createContext<ClientsContextType | undefined>(
  undefined,
);

interface ClientsProviderProps {
  children: ReactNode;
}

export const useClientsContext = (): ClientsContextType => {
  const context = useContext(ClientsContext);
  if (!context) {
    throw new Error("useClientsContext must be used within ClientsProvider");
  }
  return context;
};

const ClientsProvider: React.FC<ClientsProviderProps> = ({ children }) => {
  const [clients, setClients] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all clients with pagination
  const fetchClients = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/clients?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let clientsData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Nested structure: response.data.data.data
          clientsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Direct array: response.data.data
          clientsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData.data)) {
          clientsData = responseData.data;
        }
      } else if (Array.isArray(responseData)) {
        clientsData = responseData;
      }
      
      setClients(clientsData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClients([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new client
  const addClient = async (client: any): Promise<void> => {
    try {
      const response = await api.post("/clients", client);
      setClients((prev) => [...prev, response.data?.data || response.data]);
    } catch (error: any) {
      console.error("Error adding client:", error);
      throw error.response?.data || error;
    }
  };

  // Edit an existing client
  const editClient = async (client: any): Promise<void> => {
    try {
      const response = await api.put(`/clients/${client.id}`, client);
      const updatedClient = response.data?.data || response.data;
      setClients((prev) =>
        prev.map((item) => (item.id === client.id ? updatedClient : item))
      );
    } catch (error: any) {
      console.error("Error editing client:", error);
      throw error.response?.data || error;
    }
  };

  // Delete a client
  const deleteClient = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/clients/${id}`);
      setClients((prev) => prev.filter((item) => item.id !== id));
    } catch (error: any) {
      console.error("Error deleting client:", error);
      throw error.response?.data || error;
    }
  };

  useEffect(() => {
    fetchClients(1, 15);
  }, [fetchClients]);

  return (
    <ClientsContext.Provider
      value={{
        clients,
        pagination,
        loading,
        fetchClients,
        addClient,
        editClient,
        deleteClient,
      }}
    >
      {children}
    </ClientsContext.Provider>
  );
};

export default ClientsProvider;
